/**
 * @file An insight (for gemeente Breda) to show contract and planned hours
 * @author Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EntityQuery } from 'breeze-client';
import * as moment from 'moment';
import { DomainService } from '../../domain.service';
import { LocalisationService } from '../../core/localisation.service';
import { InsightService } from '../insight.service';
import { InsightProvider } from '../insight-provider';
import { InsightTableComponent } from '../insight-table/insight-table.component';
import { durationToText } from '../../core/helpers/durationToText';
import { ComponentRegistry } from '../../core/component-registry';
import { ResourceService } from '../../resource.service';
import { AlertsService } from '../../core/alerts.service';
import { EventsService } from '../../core/event.service';
/**
 * An insight (for gemeente Breda) to show contract and planned hours
 */
var InsightContractPlannedComponent = /** @class */ (function (_super) {
    tslib_1.__extends(InsightContractPlannedComponent, _super);
    /**
     * Creates an instance of InsightContractPlannedComponent
     */
    function InsightContractPlannedComponent(resourceService, provider, insightService, localisationService, router, domainService, translateService, alertsService, eventsService) {
        var _this = _super.call(this, resourceService, provider, insightService, localisationService, alertsService, eventsService) || this;
        _this.domainService = domainService;
        _this.translateService = translateService;
        _this.alertsService = alertsService;
        /**
         * Contains the date of horizon within the view
         * @private
         */
        _this.horizonDate = moment().startOf('day');
        _this.makeHorizonDateListener(_this.subscriptionPool, router, function (value) {
            _this.horizonDate = value;
            _this.refresh();
        });
        return _this;
    }
    /**
     * Register component by the insight provider
     */
    InsightContractPlannedComponent.register = function () {
        ComponentRegistry.register('insight', 'InsightContractPlannedComponent', InsightContractPlannedComponent);
    };
    Object.defineProperty(InsightContractPlannedComponent.prototype, "start", {
        /**
         * Gets the start of week with respect to startTimeOffset in weeks
         * @readonly
         * @type {Date}
         */
        get: function () {
            var start = this.horizonDate.clone().startOf('isoWeek');
            start.add(this.startOffset, 'days');
            return start.toDate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InsightContractPlannedComponent.prototype, "finish", {
        /**
         * Gets finish of week with respect to endTimeOffset in weeks
         * @readonly
         * @type {Date}
         */
        get: function () {
            var date = this.horizonDate.clone().startOf('isoWeek');
            date.add(this.finishOffset, 'days');
            var finish = date.toDate();
            if (finish > this.publishedTill) {
                finish = this.publishedTill;
            }
            return finish;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InsightContractPlannedComponent.prototype, "startOffset", {
        /**
         * Gets the offset of the start in weeks relative to the horizon date
         * @readonly
         * @private
         * @type {number}
         */
        get: function () {
            var offset = 0;
            // Get the offset from the configuration
            if (this.configuration && this.configuration.data && this.configuration.data.startTimeOffset) {
                offset = this.configuration.data.startTimeOffset;
            }
            return offset;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InsightContractPlannedComponent.prototype, "finishOffset", {
        /**
         * Gets the offset of the finish in weeks relative to the horizon date
         * @readonly
         * @private
         * @type {number}
         */
        get: function () {
            var offset = 0;
            // Get the offset from the configuration
            if (this.configuration && this.configuration.data && this.configuration.data.endTimeOffset) {
                offset = this.configuration.data.endTimeOffset;
            }
            return offset;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Is called when the component is destroyed
     */
    InsightContractPlannedComponent.prototype.ngOnDestroy = function () {
        this.subscriptionPool.unsubscribeAll();
        this.subscriptionPool = null;
    };
    /**
     * Is called after the configuration is loaded
     */
    InsightContractPlannedComponent.prototype.afterInitialize = function () {
        var _this = this;
        _super.prototype.afterInitialize.call(this);
        // currentUser.Resource.City.Name is 'Flex' of 'Vast'. Aan de hand daarvan bepalen of afwezigheden ook meetellen in het contract of niet.
        var resourceCityQuery = EntityQuery.from('ResourceItems').expand('City').where('Oid', '==', this.resourceService.getResourceOid());
        this.domainService.entityManager.executeQuery(resourceCityQuery).then(function (response) {
            // Untested; there are no 'Flex' cases on development....
            if (response && response['results'] && response['results'][0] && response['results'][0]['City'] && response['results'][0]['City']['Name']) {
                _this.flexible = (response['results'][0]['City']['Name'] === 'Flex');
            }
            else {
                _this.flexible = false;
            }
            _this.makeEntityChangedListener(_this.subscriptionPool, _this.domainService, function (data) {
                if (data.entity.entityType.shortName === 'Assignment' || (!_this.flexible && data.entity.entityType.shortName === 'Appointment')) {
                    _this.refresh();
                }
            });
            _this.refresh();
        });
        // });
    };
    /**
     * Sets up the items in the insight table
     */
    InsightContractPlannedComponent.prototype.afterItemsLoaded = function () {
        if (!this.resourceService.getResourceOid()) { // TODO: not needed anymore?
            return;
        }
        var items = [];
        var weekHoursValue = '?';
        if (this.loadedItems.length > 0) {
            var item = this.loadedItems[0];
            for (var index = 1; index < this.loadedItems.length; index++) {
                if (moment(this.loadedItems[index]['date']).isAfter(moment(item['date']))) {
                    item = this.loadedItems[index];
                }
            }
            weekHoursValue = item['value'] + ' ' + this.translateService.instant('APP.INSIGHTS.HOURS_PER_WEEK');
        }
        items.push({
            name: this.translateService.instant('APP.MISC.RESOURCE.CONTRACT'),
            value: weekHoursValue
        });
        items.push({
            name: this.relativeWeek(this.horizonDate),
            value: ''
        }, {
            name: this.relativeDay(this.horizonDate),
            value: ''
        });
        var include = 'Assignments !PROBABILITY[29..31]';
        if (!this.flexible) {
            include += ' Appointments';
        }
        var weekQuery = EntityQuery.from('TaskList').expand('Item');
        weekQuery.parameters = {
            className: 'Resource',
            oid: this.resourceService.getResourceOid(),
            start: this.horizonDate.clone().startOf('week'),
            finish: this.horizonDate.clone().endOf('week'),
            include: include
        };
        this.domainService.entityManager.executeQuery(weekQuery).then(function (result) {
            var effort = moment.duration(0);
            result.results.forEach(function (entity) {
                if (entity['Item']['Effort']) {
                    effort.add(entity['Item']['Effort']);
                }
            });
            items[1]['value'] = durationToText(effort);
        });
        var dayQuery = EntityQuery.from('TaskList').expand('Item');
        dayQuery.parameters = {
            className: 'Resource',
            oid: this.resourceService.getResourceOid(),
            start: this.horizonDate.clone().startOf('day'),
            finish: this.horizonDate.clone().endOf('day'),
            include: include
        };
        this.domainService.entityManager.executeQuery(dayQuery).then(function (result) {
            var effort = moment.duration(0);
            result.results.forEach(function (entity) {
                if (entity['Item']['Effort']) {
                    effort.add(entity['Item']['Effort']);
                }
            });
            items[2]['value'] = durationToText(effort);
        });
        this.setData(items);
    };
    /**
     * Returns a string representing the week relative to this week
     * @param {moment.Moment} date The date from which a string is derived
     * @returns {string} The week relative to this week
     */
    InsightContractPlannedComponent.prototype.relativeWeek = function (date) {
        switch (date.clone().startOf('week').diff(moment().startOf('week'), 'week')) {
            case -1:
                return this.translateService.instant('APP.INSIGHTS.FOOTER.LAST_WEEK');
            case 0:
                return this.translateService.instant('APP.INSIGHTS.FOOTER.THIS_WEEK');
            case 1:
                return this.translateService.instant('APP.INSIGHTS.FOOTER.NEXT_WEEK');
            default:
                return this.translateService.instant('APP.INSIGHTS.WEEK') + ' ' + date.isoWeek();
        }
    };
    /**
     * Returns a string representing the date relative to today
     * @param {moment.Moment} date The date from which a string is derived
     * @returns {string} The day relative to today.
     */
    InsightContractPlannedComponent.prototype.relativeDay = function (date) {
        switch (date.diff(moment().startOf('day'), 'day')) {
            case -1:
                return this.translateService.instant('APP.INSIGHTS.FOOTER.YESTERDAY');
            case 0:
                return this.translateService.instant('APP.INSIGHTS.FOOTER.TODAY');
            case 1:
                return this.translateService.instant('APP.INSIGHTS.FOOTER.TOMORROW');
            default:
                return date.format('L');
        }
    };
    return InsightContractPlannedComponent;
}(InsightTableComponent));
export { InsightContractPlannedComponent };
